import { dependencies } from '@pn/core/dependencies';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { createSelector } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';
import {
  companyMonthlyStatsSlice,
  companyQuarterlyCorporateStatsSlice,
  CompanyStatsState,
} from './companyStatsSlice';
import type {
  ICompanyStatisticsActions,
  ICompanyStatisticsStorage,
} from './ports';

export const useReduxQuarterlyStatsStorage = (): ICompanyStatisticsStorage => {
  const { name, getInitialState } = companyQuarterlyCorporateStatsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    allStats: useSliceSelector(selectAllStats),
    companyStats: useSliceSelector((state) => state.companyStats),
    loadedCompanyIds: useSliceSelector((state) => state.loadedCompanyIds),
  };
};

export const reduxQuarterlyStatsActions = (): ICompanyStatisticsActions => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = companyQuarterlyCorporateStatsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    receive: (loadedCompanyId, stats) =>
      dispatch(actions.receive({ loadedCompanyId, stats })),
    error: () => dispatch(actions.error()),
    reset: () => dispatch(actions.reset()),
  };
};

export const useReduxMonthlyStatsStorage = (): ICompanyStatisticsStorage => {
  const { name, getInitialState } = companyMonthlyStatsSlice;

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isError: useSliceSelector((state) => state.isError),
    allStats: useSliceSelector(selectAllStats),
    companyStats: useSliceSelector((state) => state.companyStats),
    loadedCompanyIds: useSliceSelector((state) => state.loadedCompanyIds),
  };
};

export const reduxMonthlyStatsActions = (): ICompanyStatisticsActions => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = companyMonthlyStatsSlice;

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: () => dispatch(actions.request()),
    receive: (loadedCompanyId, stats) =>
      dispatch(actions.receive({ loadedCompanyId, stats })),
    error: () => dispatch(actions.error()),
    reset: () => dispatch(actions.reset()),
  };
};

const selectAllStats = createSelector(
  [(state: CompanyStatsState) => state.companyStats],
  (queries) => {
    return Object.values(queries).flat();
  }
);
